import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import * as moment from 'moment';

import { Subject } from 'rxjs';

import { ConfigService } from './config.service';
import { Constants } from '../utilities/constants.util';
import { CostCenter } from '../models/cost-center.model';
import { MiscUtil } from '../utilities/misc.util';
import { ShiftSchedule } from '../models/shift-schedule.model';
import { StaffingGridUtil } from 'src/app/smt/staffing/staffing-grid/utilities/staffing-grid.util';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';

@Injectable({
    providedIn: 'root'
})
export class ShiftScheduleService {
    readonly apiServer: string;

    schedules: ShiftSchedule[];

    private schedulesSource = new Subject<ShiftSchedule[]>();
    public schedules$ = this.schedulesSource.asObservable();

    constructor(private _configService: ConfigService, private _http: HttpClient) {
        this.apiServer = this._configService.appSettings.webApiServer + "api/ShiftSchedule/";
    }

    announceSchedules(schedules: ShiftSchedule[]) {   
        if (!MiscUtil.checkArray(schedules)) {
            return;
        }

        this.schedules = schedules;
        this.schedulesSource.next(schedules);
    }

    private checkAcuitySchedules(costCenter: CostCenter, schedules: ShiftSchedule[]): boolean {
        if (!WorkflowPageUtil.hasAcuityConfiguration(costCenter) && !WorkflowPageUtil.hasAcuityWithRoomsConfiguration(costCenter)) {
            return false;
        }

        for(let counter = 0; counter < schedules.length; counter++) {
            let schedule = schedules[counter];

            if (!MiscUtil.checkArray(schedule.staffRatios) || !StaffingGridUtil.checkCompleteAcuityGrid(costCenter, schedule)) {
                return false;
            }
        }
        
        return true;
    }

    private checkAddOnSchedules(costCenter: CostCenter, schedules: ShiftSchedule[]): boolean {
        if (!WorkflowPageUtil.hasUosConfiguration(costCenter) || !costCenter.ancillaryConfiguration.addOns) {
            return false;
        }

        for(let counter = 0; counter < schedules.length; counter++) {
            let schedule = schedules[counter];  
            let shiftBlocks = StaffingGridUtil.setPartialShiftBlocks(schedule);

            if (!StaffingGridUtil.checkCompleteAddOnGrid(shiftBlocks)) {
                return false;
            }
        }

        return true;
    }

    private checkFixedSchedules(costCenter: CostCenter, schedules: ShiftSchedule[]): boolean {
        for(let counter = 0; counter < schedules.length; counter++) {
            let schedule = schedules[counter];
            let shiftBlocks = StaffingGridUtil.setPartialShiftBlocks(schedule);

            if (!StaffingGridUtil.isJobShiftGridComplete(costCenter.fixedJobTitles, shiftBlocks, true, 0, 0)) {
                return false;
            }
        }

        return true;
    }

    private checkInpatientSchedules(costCenter: CostCenter, schedules: ShiftSchedule[]): boolean {
        if (!WorkflowPageUtil.hasInpatientConfiguration(costCenter)) {
            return false;
        }

        let maxCensus = costCenter.inpatientConfiguration.maxCensus;
        let minCensus = costCenter.inpatientConfiguration.minCensus;

        for(let counter = 0; counter < schedules.length; counter++) {
            let schedule = schedules[0];
            let shiftBlocks = StaffingGridUtil.setPartialShiftBlocks(schedule);

            if (!StaffingGridUtil.isJobShiftGridComplete(costCenter.variableJobTitles, shiftBlocks, false, maxCensus, minCensus)) {
                return false;
            }
        }

        return true;
    }

    checkSaturdaySchedules(shiftSchedules: ShiftSchedule[]): boolean {
        return shiftSchedules.some(s => s.isSaturday && s.isValid && !s.inactiveDate);
    }

    async checkCostCenterSchedules(costCenter: CostCenter) : Promise<boolean> {
        var shiftSchedules = await this.getActiveShiftSchedulesByCostCenter(costCenter.id);

        return this.checkSchedules(costCenter, shiftSchedules);
    }

    checkSchedules(costCenter: CostCenter, schedules: ShiftSchedule[]) : boolean {
        if (!MiscUtil.checkArray(schedules) || !this.schedulesSpanWeek(schedules)) {
            return false;
        }
        
        schedules = schedules.filter(s => !s.isClosed);        

        // All cost center types currently have the same fixed jobs grid so check that first.
        if (MiscUtil.checkArray(costCenter.fixedJobTitles) && !this.checkFixedSchedules(costCenter, schedules)) {
            return false;
        }
                
        return this.checkSchedulesByCostCenterConfiguration(costCenter, schedules);
    }    

    private checkSchedulesByCostCenterConfiguration(costCenter: CostCenter, schedules: ShiftSchedule[]): boolean {
        let usesAcuity = WorkflowPageUtil.hasAcuityConfiguration(costCenter) || WorkflowPageUtil.hasAcuityWithRoomsConfiguration(costCenter);
        let usesInpatient = WorkflowPageUtil.hasInpatientConfiguration(costCenter);
        let usesTotalUos = WorkflowPageUtil.hasUosConfiguration(costCenter); 
        let hasVariableJobs = MiscUtil.checkArray(costCenter.variableJobTitles)   ;

        if (usesInpatient) {
            return !hasVariableJobs || this.checkInpatientSchedules(costCenter, schedules);
        }
        else if (usesTotalUos) {
            if (costCenter.ancillaryConfiguration.addOns && !this.checkAddOnSchedules(costCenter, schedules)) {
                return false;                
            }

            return !hasVariableJobs || this.checkTotalUosSchedules(costCenter, schedules);
        }
        else if (usesAcuity) {
            return !hasVariableJobs || this.checkAcuitySchedules(costCenter, schedules);
        }

        return true;
    }

    private checkTotalUosSchedules(costCenter: CostCenter, schedules: ShiftSchedule[]): boolean {
        if (!WorkflowPageUtil.hasUosConfiguration(costCenter)) {
            return false;
        }

        for(let counter = 0; counter < schedules.length; counter++) {
            let schedule = schedules[counter];

            if (!MiscUtil.checkArray(schedule.staffRatios) || schedule.staffRatios.some(r => 
                                                                                    !r.inactiveDate && 
                                                                                    (!MiscUtil.isValidNonNegativeNumber(r.staffValue) || 
                                                                                    !MiscUtil.isValidNonNegativeNumber(r.uosCount)))) {
                return false;
            }
        }

        return true;
    }

    async getActiveShiftSchedulesByCostCenter(costCenterId: number): Promise<ShiftSchedule[]> {
        const params = new HttpParams().set('costCenterId', costCenterId.toString());

        return this._http.get<ShiftSchedule[]>(`${this.apiServer}GetActiveShiftSchedulesByCostCenter`, { params: params }).toPromise();
    }

    async setShiftSchedules(schedules: ShiftSchedule[]) : Promise<boolean> {
        return this._http.post<boolean>(`${this.apiServer}SetShiftSchedules`, schedules).toPromise();
    }    
    
    public areSchedulesPresent(schedules: ShiftSchedule[]) : boolean {
        return !!schedules && !!schedules.length;
    }

    schedulesSpanWeek(schedules: ShiftSchedule[]): boolean {
        // Check that schedules span the entire week.
        return schedules.some(s => s.isWeekday) && 
            (schedules.some(s => s.isWeekend) || (schedules.some(s => s.isSaturday) && schedules.some(s => s.isSunday)));
    }
}
